import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaBook, FaBriefcase, FaBus, FaGavel, FaGlobe, FaGraduationCap, FaHeart,
  FaHourglassHalf, FaLightbulb, FaMapMarkerAlt, FaPlane, FaRocket, FaSearch,
  FaStar, FaTrain, FaTrophy, FaArrowDown, FaArrowRight,
} from 'react-icons/fa';
import './JourneySection.css';
import imgJourney     from '../assets/images/Journey.jpeg';
import imgKnowMe      from '../assets/images/KnowMoreAboutMe.jpeg';
import imgSkills      from '../assets/images/Skills.jpeg';
import imgLetsConnect from '../assets/images/LetsConnect.jpeg';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';

const ICON_MAP = {
  mapmarker:  FaMapMarkerAlt,
  gavel:      FaGavel,
  hourglass:  FaHourglassHalf,
  search:     FaSearch,
  bus:        FaBus,
  train:      FaTrain,
  plane:      FaPlane,
  briefcase:  FaBriefcase,
  star:       FaStar,
  heart:      FaHeart,
  globe:      FaGlobe,
  book:       FaBook,
  trophy:     FaTrophy,
  rocket:     FaRocket,
  lightbulb:  FaLightbulb,
  graduation: FaGraduationCap,
};

const DEFAULT_CHAPTERS = [
  {
    num: '01',
    tag: 'THE SPARK',
    stampLabel: 'ENTRY · ODISHA',
    year: '2016',
    title: 'Where It All Began',
    story:
      "I was born in Odisha and raised between Bhubaneswar and Jamshedpur, before heading to Delhi for my B.Com. While commerce made sense, it was law that sparked something deeper. I discovered that while numbers can add up, it’s words that can move people.",
    quote: 'Numbers added up — but words moved people.',
    detail: 'Odisha → Delhi → Law',
    visaLabel: 'ENTRY GRANTED',
    Icon: FaMapMarkerAlt,
    accent: '#C25B2A',
    stampColor: '#C25B2A',
    photo: imgKnowMe,
    imgPos: 'center top',
  },
  {
    num: '02',
    tag: 'THE BUILD',
    stampLabel: 'VERIFIED · SCHOLAR',
    year: '2017–19',
    title: 'Law, Leadership & Leap',
    story:
      'From standing first in my law school batch to leading student councils and winning scholarships. I dove deep into the legal world — and loved it: I gained real-world experience in top-Indian firms like Wipro and WNS Denali, managing contracts, ensuring compliance, and learning what it means to solve problems before they happen.',
    quote: 'I learned to solve problems before they happen.',
    detail: 'Wipro · WNS Denali · Scholar',
    visaLabel: 'MERIT VERIFIED',
    Icon: FaGavel,
    accent: '#b88822',
    stampColor: '#b88822',
    photo: imgSkills,
    imgPos: 'center center',
    flip: true,
  },
  {
    num: '03',
    tag: 'THE PAUSE',
    stampLabel: 'DELAYED · RESILIENCE',
    year: '2020–22',
    title: 'The Dream, Delayed – Not Denied',
    story:
      '2020 was supposed to be my London chapter — my LLM at Queen Mary. But the world shut down. I stayed back, kept working, and when the time was right, I tried again. In 2022, I landed in London — two years late, but with double the passion. I completed my LLM with Distinction while working part-time roles and building a life from scratch.',
    quote: 'When the window reopened, I flew.',
    detail: 'Queen Mary · London · Resilience',
    visaLabel: 'RESILIENCE',
    Icon: FaHourglassHalf,
    accent: '#C25B2A',
    stampColor: '#8B4513',
    photo: imgJourney,
    imgPos: 'center top',
  },
  {
    num: '04',
    tag: 'THE SHIFT',
    stampLabel: 'SEARCH · SELF-AUTHORED',
    year: '2024+',
    title: 'The Search, The Shift',
    story:
      'Post-LLM, I dove into the job hunt — CVs, interviews, rejection emails. I had the experience, the degree, the drive — but something still wasn’t clicking. That’s when I stopped chasing and started building. This website is my shift — from applicant to author of my own narrative.',
    quote: 'I stopped chasing and started building.',
    detail: 'Applicant → Author · Now',
    visaLabel: 'AUTHOR MODE',
    Icon: FaSearch,
    accent: '#D4AF37',
    stampColor: '#D4AF37',
    photo: imgLetsConnect,
    imgPos: 'center center',
    flip: true,
    isFinal: true,
  },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/* Smooth S-curve through every stop. Each segment only moves downward,
   so the path's y always increases — used for scroll lookup below. */
function buildRoute(points) {
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const p = points[i - 1];
    const q = points[i];
    const mid = (q.y - p.y) / 2;
    d += ` C ${p.x} ${p.y + mid}, ${q.x} ${q.y - mid}, ${q.x} ${q.y}`;
  }
  return d;
}

export default function JourneySection() {
  const [chapters, setChapters] = useState(DEFAULT_CHAPTERS);
  const [route, setRoute] = useState({ d: '', w: 0, h: 0, ys: [] });
  const [drawn, setDrawn] = useState({ len: 0, total: 0, x: 0, y: 0, angle: 90 });
  const [reached, setReached] = useState(0);
  const [revealed, setRevealed] = useState(() => new Set());
  const routeRef = useRef(null);
  const pathRef = useRef(null);
  const panelRefs = useRef([]);
  const dotRefs = useRef([]);

  const n = chapters.length;
  const activeIdx = Math.min(Math.max(reached - 1, 0), n - 1);

  /* Fetch dynamic phases — replace defaults if Firestore has data */
  useEffect(() => {
    getDocs(query(collection(db, 'journey_phases'), orderBy('order', 'asc')))
      .then(snap => {
        if (snap.empty) return;
        setChapters(snap.docs.map(d => {
          const data = d.data();
          /* Ensure num exists — derive from order if missing */
          const num = data.num || String(data.order || 1).padStart(2, '0');
          return { ...data, num, id: d.id, Icon: ICON_MAP[data.icon] || FaMapMarkerAlt };
        }));
      })
      .catch(() => {});
  }, []);

  /* Measure every stop and draw the route through them. */
  const measure = useCallback(() => {
    const box = routeRef.current;
    if (!box) return;
    const b = box.getBoundingClientRect();
    const points = dotRefs.current.slice(0, n + 1).filter(Boolean).map(el => {
      const r = el.getBoundingClientRect();
      return {
        x: Math.round(r.left - b.left + r.width / 2),
        y: Math.round(r.top - b.top + r.height / 2),
      };
    });
    if (points.length < 2) return;
    const d = buildRoute(points);
    const w = Math.round(b.width);
    const h = Math.round(b.height);
    setRoute(prev => (prev.d === d && prev.w === w && prev.h === h
      ? prev
      : { d, w, h, ys: points.map(p => p.y) }));
  }, [n]);

  useLayoutEffect(() => {
    measure();
    const box = routeRef.current;
    let ro;
    if (box && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(box);
    }
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, chapters]);

  /* Draw the route up to the middle of the screen and fly the plane there. */
  const update = useCallback(() => {
    const box = routeRef.current;
    const path = pathRef.current;
    const { ys } = route;
    if (!box || !path || !ys.length) return;
    const total = path.getTotalLength();
    const target = window.innerHeight * 0.5 - box.getBoundingClientRect().top;

    let len;
    if (target <= ys[0]) len = 0;
    else if (target >= ys[ys.length - 1]) len = total;
    else {
      let lo = 0;
      let hi = total;
      for (let k = 0; k < 22; k++) {
        const mid = (lo + hi) / 2;
        if (path.getPointAtLength(mid).y < target) lo = mid; else hi = mid;
      }
      len = lo;
    }

    const pt = path.getPointAtLength(len);
    const a = path.getPointAtLength(Math.max(0, len - 2));
    const b = path.getPointAtLength(Math.min(total, len + 2));
    const angle = a.x === b.x && a.y === b.y ? 90 : (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;

    setDrawn({ len, total, x: pt.x, y: pt.y, angle });
    setReached(ys.filter(y => y <= target + 1).length);
  }, [route]);

  useEffect(() => {
    update();
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; update(); });
    };
    /* body is the scroller on this site, not window — a capturing
       listener on document hears scrolls from either. */
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
    };
  }, [update]);

  /* Fade each chapter in the first time it comes into view. */
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const i = Number(entry.target.dataset.index);
        setRevealed(prev => (prev.has(i) ? prev : new Set(prev).add(i)));
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    panelRefs.current.slice(0, n + 1).filter(Boolean).forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [n]);

  const scrollTo = (i) =>
    panelRefs.current[i]?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'center',
    });

  const ready = Boolean(route.d) && drawn.total > 0;
  const progress = ready ? Math.round((drawn.len / drawn.total) * 100) : 0;

  return (
    <main className="journey-section">
      <nav className="journey-chapter-nav" aria-label="Journey chapters">
        <div className="jcn-inner">
          {chapters.map((ch, i) => (
            <button
              key={ch.id || ch.num}
              type="button"
              className={`jcn-stop${activeIdx === i ? ' active' : ''}${i < reached ? ' completed' : ''}`}
              aria-label={`Chapter ${ch.num}: ${ch.title}`}
              title={ch.title}
              onClick={() => scrollTo(i)}
              aria-current={activeIdx === i ? 'step' : undefined}
            >
              <span>{ch.num}</span>
              <strong>{ch.tag}</strong>
            </button>
          ))}
        </div>
        <div className="jcn-progress" aria-hidden="true"><span style={{ transform: `scaleX(${progress / 100})` }} /></div>
      </nav>

      <header className="journey-header">
        <p><FaPlane aria-hidden="true" /> A journey still unfolding</p>
        <h1>My Journey</h1>
        <span>Clarity · Strategy · Growth</span>
        <div className="journey-intro">From a spark of curiosity to a story of my own. The places, lessons and leaps that shaped the way I work.</div>
        <button type="button" className="jr-start" onClick={() => scrollTo(0)}>Explore my story <FaArrowDown aria-hidden="true" /></button>
        <div className="journey-overview"><span>{String(n).padStart(2, '0')} chapters</span><span>India to London</span><span>Still unfolding</span></div>
      </header>

      <section
        className={`journey-route${route.d && typeof IntersectionObserver !== 'undefined' ? ' is-ready' : ''}`}
        ref={routeRef}
        aria-label="Journey story"
      >
        {route.d && (
          <svg
            className="jr-svg"
            width={route.w}
            height={route.h}
            viewBox={`0 0 ${route.w} ${route.h}`}
            aria-hidden="true"
          >
            <path className="jr-path-base" d={route.d} />
            <path
              className="jr-path-drawn"
              ref={pathRef}
              d={route.d}
              style={ready
                ? { strokeDasharray: drawn.total, strokeDashoffset: drawn.total - drawn.len }
                : { opacity: 0 }}
            />
          </svg>
        )}
        {ready && (
          <FaPlane
            className="jr-plane"
            aria-hidden="true"
            style={{
              opacity: drawn.len > 30 && drawn.len < drawn.total - 30 ? 1 : 0,
              transform: `translate(${drawn.x}px, ${drawn.y}px) translate(-50%, -50%) rotate(${drawn.angle}deg)`,
            }}
          />
        )}

        {chapters.map((ch, i) => {
          const Icon = ch.Icon || FaMapMarkerAlt;
          const place = ch.place || ch.detail;
          const cls = [
            'jr-chapter',
            i % 2 === 0 ? 'jr-left' : 'jr-right',
            revealed.has(i) ? 'is-in' : '',
            i < reached ? 'is-reached' : '',
          ].filter(Boolean).join(' ');
          return (
            <article
              key={ch.id || ch.num}
              className={cls}
              data-index={i}
              ref={(el) => (panelRefs.current[i] = el)}
            >
              <div className="jr-stop" aria-hidden="true">
                <span className="jr-dot" ref={(el) => (dotRefs.current[i] = el)}><Icon /></span>
              </div>
              <div className="journey-photo">
                {ch.photo && (
                  <img
                    src={ch.photo}
                    alt={ch.title}
                    width="800"
                    height="600"
                    loading={i === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    style={{ objectPosition: ch.imgPos || 'center' }}
                  />
                )}
              </div>
              <div className="journey-copy">
                <div className="journey-meta"><span>{ch.num}</span><strong>{ch.tag}</strong><em>{ch.year}</em></div>
                {place && <p className="jr-place"><FaMapMarkerAlt aria-hidden="true" /> {place}</p>}
                <h2>{ch.title}</h2>
                <p className="jr-story">{ch.story}</p>
                {ch.quote && <blockquote>{ch.quote}</blockquote>}
                <button type="button" className="jr-continue" onClick={() => scrollTo(i + 1)}>
                  {i === n - 1 ? 'The next chapter' : 'Next chapter'} <FaArrowRight aria-hidden="true" />
                </button>
              </div>
            </article>
          );
        })}

        <article
          className={`jr-chapter jr-next${revealed.has(n) ? ' is-in' : ''}${reached > n ? ' is-reached' : ''}`}
          data-index={n}
          ref={(el) => (panelRefs.current[n] = el)}
        >
          <div className="jr-stop" aria-hidden="true">
            <span className="jr-dot jr-dot-open" ref={(el) => (dotRefs.current[n] = el)}><FaPlane /></span>
          </div>
          <div className="journey-copy">
            <p className="jr-next-kicker">Next stop</p>
            <h2>Still being written.</h2>
            <div className="jr-next-links">
              <Link to="/guidance" className="jr-btn jr-btn-solid">Mentoring</Link>
              <Link to="/blog" className="jr-btn">Read The PP Edit</Link>
            </div>
          </div>
        </article>
      </section>
    </main>
  );
}
